<template>
  <v-dialog
    v-model="showEditEmployeeDialog"
    max-width="800px"
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <v-card>
      <form>
        <v-card-title class="primary white--text">
          <h3 class="headline mb-0">
            {{ $t('texts.employee.edit') }} - {{ employee.employee_first_name }} {{ employee.employee_last_name }}
          </h3>
          <v-spacer/>
          <v-btn
            text small fab
            @click="showEditEmployeeDialog = false, selectedEmployee = {}"
          >
            <v-icon color="white">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-container class="pa-0">
            <v-row>
              <v-col :cols="12">
                <v-radio-group
                  v-model="editEmployeeValues"
                  hide-details
                >
                  <v-radio
                    :label="$t('texts.employee.employmentChange')"
                    value="EDIT"
                    color="primary"
                  />
                  <v-radio
                    :label="$t('texts.employee.employmentTerminated')"
                    value="CANCEL"
                    color="primary"
                    v-if="employee.type === 'current'"
                  />
                  <v-radio
                    :label="$t('texts.employee.employmentDidNotTakeUp')"
                    value="DELETE"
                    color="primary"
                    v-if="employee.type === 'current'"
                  />
                  <v-radio
                    :label="$t('texts.employee.employmentWillNotTakeUp')"
                    value="DELETE"
                    color="primary"
                    v-else
                  />
                </v-radio-group>
              </v-col>
            </v-row>
            <v-row>
              <v-col :cols="12">
                <v-alert type="error" :value="alert">
                  {{ alertText }}
                </v-alert>
              </v-col>
            </v-row>
            <v-row>
              <v-col :cols="12">
                <v-card flat v-if="editEmployeeValues === 'EDIT'">
                  <v-card-text>
                    <v-container grid-list-xl class="pa-0">
                      <form>
                        <v-row>
                          <v-col :cols="6">
                            <v-text-field
                              v-model="employee.title"
                              :label="$t('texts.insertJobtitle')"
                              required
                              :error-messages="errors.collect('title')"
                              v-validate="'required'"
                              data-vv-name="title"
                              :data-vv-as="$t('texts.title')"
                            />
                          </v-col>
                          <v-col :cols="12" :sm="6">
                              <v-text-field
                                v-model="employee.workload"
                                :label="$t('texts.percent')"
                                suffix="%"
                                required
                                :error-messages="errors.collect('percent')"
                                v-validate="'required|numeric|min_value:1|max_value:100'"
                                data-vv-name="percent"
                                :data-vv-as="$t('texts.percent')"
                              />
                            </v-col>
                        </v-row>
                        <v-row>
                          <v-col :cols="6">
                            <v-menu
                              ref="startDate"
                              :close-on-content-click="false"
                              v-model="startDatePicker"
                              transition="scale-transition"
                              offset-y
                              min-width="290px"
                              :return-value.sync="employee.start_date"
                            >
                              <template v-slot:activator="{ on }">
                                <v-text-field
                                  v-on="on"
                                  ref="startDateToValidate"
                                  :label="$t('texts.startOfWork')"
                                  :value="employee.start_date | formatDateMedium"
                                  readonly
                                  required
                                  :error-messages="errors.collect('startDate')"
                                />
                              </template>
                              <v-date-picker
                                v-model="employee.start_date"
                                @input="$refs.startDate.save(employee.start_date)"
                                header-color="primary"
                                color="primary"
                                locale="de-ch"
                                :first-day-of-week="1"
                                ref="startDatePicker"
                              />
                            </v-menu>
                            <input
                              hidden
                              v-model="employee.start_date"
                              ref="startDateInput"
                              v-validate="'required|date_format:yyyy-MM-dd'"
                              data-vv-name="startDate"
                              :data-vv-as="$t('texts.startOfWork')"
                            />
                          </v-col>
                          <v-col :cols="6">
                            <v-menu
                              ref="endDate"
                              :close-on-content-click="false"
                              v-model="endDatePicker"
                              transition="scale-transition"
                              offset-y
                              min-width="290px"
                              :return-value.sync="employee.end_date"
                            >
                              <template v-slot:activator="{ on }">
                                <v-text-field
                                  v-on="on"
                                  :label="$t('texts.endOfWork')"
                                  :value="employee.end_date | formatDateMedium"
                                  readonly
                                  required
                                  :error-messages="errors.collect('endDate')"
                                />
                              </template>
                              <v-date-picker
                                v-model="employee.end_date"
                                @input="$refs.endDate.save(employee.end_date)"
                                header-color="primary"
                                color="primary"
                                locale="de-ch"
                                :first-day-of-week="1"
                                ref="endDatePicker"
                                :min="endDatePickerMin"
                                :max="endDatePickerMax"
                              />
                            </v-menu>
                            <input
                              hidden
                              v-model="employee.end_date"
                              v-validate="'required|date_format:yyyy-MM-dd|after:startDateInput|season:startDateInput'"
                              data-vv-name="endDate"
                              :data-vv-as="$t('texts.endOfWork')"
                            />
                          </v-col>
                        </v-row>
                      </form>
                    </v-container>
                  </v-card-text>
                </v-card>
                <v-card flat v-if="editEmployeeValues === 'CANCEL'">
                  <v-card-text>
                    <v-container grid-list-xl class="pa-0">
                      <v-row>
                        <v-col :cols="6">
                          <v-text-field
                            :value="employee.end_date | formatDateMedium"
                            :label="$t('texts.startOfWork')"
                            disabled
                          />
                        </v-col>
                        <v-col :cols="6">
                          <v-menu
                            ref="endDate"
                            :close-on-content-click="false"
                            v-model="endDatePicker"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                            :return-value.sync="employee.end_date"
                          >
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                v-on="on"
                                :label="$t('texts.endOfWork')"
                                :value="employee.end_date | formatDateMedium"
                                readonly
                                required
                                :error-messages="errors.collect('endDate')"
                              />
                            </template>
                            <v-date-picker
                              v-model="employee.end_date"
                              @input="$refs.endDate.save(employee.end_date)"
                              header-color="primary"
                              color="primary"
                              locale="de-ch"
                              :first-day-of-week="1"
                              landscape
                              :min="endDatePickerMin"
                              :max="endDatePickerMax"
                              ref="endDatePicker"
                              v-validate="'required'"
                              data-vv-name="endDate"
                              :data-vv-as="$t('texts.endOfWork')"
                            />
                          </v-menu>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            color="primary"
            v-on:click="validateBeforeSubmit()"
            :loading="storeEmployeePending || deleteEmployeePending"
            :disabled="storeEmployeePending || deleteEmployeePending"
          >
            {{ $t('texts.save') }}
            <v-icon right class="white--text">save</v-icon>
          </v-btn>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';
import { createNamespacedHelpers } from 'vuex';

const { mapState: mapCompanyEmployeesState, mapActions: mapCompanyEmployeesActions } = createNamespacedHelpers('company/employees');

export default {
  name: 'EditEmployeeDialog',
  data() {
    return {
      showEditEmployeeDialog: false,
      employee: {
        start_date: '',
        end_date: '',
      },
      editEmployeeValues: 'EDIT',
      startDatePicker: false,
      endDatePicker: false,
      alert: false,
      alertText: '',
    };
  },
  $_veeValidate: {
    validator: 'new',
  },
  mounted() {
    this.$eventBus.$on('show-edit-employee-dialog', (item) => {
      Object.assign(this.employee, item);
      this.employee.start_date = moment(item.start_date).format('YYYY-MM-DD');
      this.employee.end_date = moment(item.end_date).format('YYYY-MM-DD');
      this.showEditEmployeeDialog = true;
    });
  },
  computed: {
    ...mapCompanyEmployeesState([
      'storeEmployeePending',
      'deleteEmployeePending',
    ]),
    selectedCompanyId() {
      return this.$route.params.companyId;
    },
    startDatePickerMin() {
      return moment().subtract(9, 'months').format('YYYY-MM');
    },
    startDatePickerMax() {
      return moment().add(36, 'months').format('YYYY-MM');
    },
    endDatePickerMin() {
      return this.employee.start_date !== null ? this.employee.start_date : '';
    },
    endDatePickerMax() {
      return moment(this.employee.start_date).add(10, 'months').format('YYYY-MM-DD');
    },
  },
  methods: {
    ...mapCompanyEmployeesActions([
      'storeEmployee',
      'deleteEmployee',
    ]),
    checkJobLength() {
      const end = moment(this.employee.end_date);
      const start = moment(this.employee.start_date);
      if (end.diff(start, 'months') > 9) {
        this.alert = true;
        this.alertText = this.$i18n.t('texts.maximalDurationExceeded');
        return false;
      }
      this.alert = false;
      this.alertText = '';
      return true;
    },
    validateBeforeSubmit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.editEmployeeValues === 'DELETE') {
            this.delete();
          } else {
            this.update();
          }
        }
      });
    },
    update() {
      if (this.checkJobLength()) {
        const payload = {
          mutation: this.editEmployeeValues,
          companyId: this.selectedCompanyId,
          type: this.employee.type.toUpperCase(),
          employee: this.employee,
        };
        const list = {
          type: this.$route.name === 'company.dashboard' ? 'cooperationAgreements' : 'employees',
        };
        this.storeEmployee({ payload, list }).then(() => {
          this.showEditEmployeeDialog = false;
        });
      }
    },
    delete() {
      const payload = {
        mutation: this.editEmployeeValues,
        companyId: this.selectedCompanyId,
        type: this.employee.type.toUpperCase(),
        employee: this.employee,
      };
      const list = {
        type: this.$route.name === 'company.dashboard' ? 'cooperationAgreements' : 'employees',
      };
      this.deleteEmployee({ payload, list }).then(() => {
        this.showEditEmployeeDialog = false;
      });
    },
  },
};
</script>
